<script>
import { mapGetters } from 'vuex';
import LoadingState from './components/widgets/LoadingState.vue';
import NetworkNotification from './components/NetworkNotification.vue';
import UpdateBanner from './components/app/UpdateBanner.vue';
import StatusBanner from './components/app/StatusBanner.vue';
import PaymentPendingBanner from './components/app/PaymentPendingBanner.vue';
import PendingEmailVerificationBanner from './components/app/PendingEmailVerificationBanner.vue';
import vueActionCable from './helper/actionCable';
import { useRouter } from 'vue-router';
import { useStore } from 'dashboard/composables/store';
import WootSnackbarBox from './components/SnackbarContainer.vue';
import {
  setColorTheme,
  DASHBOARD_THEME_CHANGE_EVENT,
  isDarkThemeEnabled,
} from './helper/themeHelper';
import {
  resolveAccountBrandingLoginPath,
  resolveAccountBrandingLogo,
} from './helper/accountBranding';
import { isOnOnboardingView } from 'v3/helpers/RouteHelper';
import { useAccount } from 'dashboard/composables/useAccount';
import { useFontSize } from 'dashboard/composables/useFontSize';
import {
  registerSubscription,
  verifyServiceWorkerExistence,
} from './helper/pushHelper';
import ReconnectService from 'dashboard/helper/ReconnectService';
import { useUISettings } from 'dashboard/composables/useUISettings';

export default {
  name: 'App',

  components: {
    LoadingState,
    NetworkNotification,
    UpdateBanner,
    StatusBanner,
    PaymentPendingBanner,
    WootSnackbarBox,
    PendingEmailVerificationBanner,
  },
  setup() {
    const router = useRouter();
    const store = useStore();
    const { accountId } = useAccount();
    // Use the font size composable (it automatically sets up the watcher)
    const { currentFontSize } = useFontSize();
    const { uiSettings } = useUISettings();

    return {
      router,
      store,
      currentAccountId: accountId,
      currentFontSize,
      uiSettings,
    };
  },
  data() {
    return {
      latestChatwootVersion: null,
      reconnectService: null,
    };
  },
  computed: {
    ...mapGetters({
      getAccount: 'accounts/getAccount',
      isRTL: 'accounts/isRTL',
      currentUser: 'getCurrentUser',
      authUIFlags: 'getAuthUIFlags',
    }),
    hideOnOnboardingView() {
      return !isOnOnboardingView(this.$route);
    },
  },

  watch: {
    currentAccountId: {
      immediate: true,
      handler() {
        if (this.currentAccountId) {
          this.initializeAccount();
        }
      },
    },
  },
  mounted() {
    this.initializeColorTheme();
    this.listenToThemeChanges();
    window.addEventListener(
      DASHBOARD_THEME_CHANGE_EVENT,
      this.handleThemeChange
    );
    // If user locale is set, use it; otherwise use account locale
    this.setLocale(
      this.uiSettings?.locale || window.chatwootConfig.selectedLocale
    );
  },
  unmounted() {
    window.removeEventListener(
      DASHBOARD_THEME_CHANGE_EVENT,
      this.handleThemeChange
    );
    if (this.reconnectService) {
      this.reconnectService.disconnect();
    }
  },
  methods: {
    initializeColorTheme() {
      setColorTheme(window.matchMedia('(prefers-color-scheme: dark)').matches);
    },
    listenToThemeChanges() {
      const mql = window.matchMedia('(prefers-color-scheme: dark)');
      mql.onchange = e => setColorTheme(e.matches);
    },
    setLocale(locale) {
      if (locale) {
        this.$root.$i18n.locale = locale;
      }
    },
    async initializeAccount() {
      await this.$store.dispatch('accounts/get');
      this.$store.dispatch('setActiveAccount', {
        accountId: this.currentAccountId,
      });
      const account = this.getAccount(this.currentAccountId);
      const { locale, latest_chatwoot_version: latestChatwootVersion } =
        account;
      const { pubsub_token: pubsubToken } = this.currentUser || {};
      // If user locale is set, use it; otherwise use account locale
      this.setLocale(this.uiSettings?.locale || locale);
      this.setDocumentTitle(account);
      this.setFavicon(account);
      this.setLogoutRedirectLink(account);
      this.latestChatwootVersion = latestChatwootVersion;
      vueActionCable.init(this.store, pubsubToken);
      this.reconnectService = new ReconnectService(this.store, this.router);
      window.reconnectService = this.reconnectService;

      verifyServiceWorkerExistence(registration =>
        registration.pushManager.getSubscription().then(subscription => {
          if (subscription) {
            registerSubscription();
          }
        })
      );
    },
    setDocumentTitle(account) {
      const brandedName =
        account?.settings?.custom_branding?.brand_name ||
        window.globalConfig?.INSTALLATION_NAME;
      if (brandedName) {
        document.title = brandedName;
      }
    },
    setFavicon(account) {
      const brandedLogo = resolveAccountBrandingLogo({
        branding: account?.settings?.custom_branding,
        globalConfig: window.globalConfig,
        isDark: isDarkThemeEnabled(),
      });

      if (!brandedLogo) return;

      window.currentFaviconUrl = brandedLogo;
      document
        .querySelectorAll('link[rel~="icon"]')
        .forEach(link => link.setAttribute('href', brandedLogo));
    },
    setLogoutRedirectLink(account) {
      window.globalConfig = window.globalConfig || {};
      window.globalConfig.LOGOUT_REDIRECT_LINK =
        resolveAccountBrandingLoginPath({
          branding: account?.settings?.custom_branding,
          fallback: window.globalConfig.LOGOUT_REDIRECT_LINK,
        });
    },
    handleThemeChange() {
      if (!this.currentAccountId) return;

      this.setFavicon(this.getAccount(this.currentAccountId));
    },
  },
};
</script>

<template>
  <div
    v-if="!authUIFlags.isFetching"
    id="app"
    class="flex flex-col w-full h-screen min-h-0 bg-n-background"
    :dir="isRTL ? 'rtl' : 'ltr'"
  >
    <!-- <UpdateBanner :latest-chatwoot-version="latestChatwootVersion" /> -->
    <template v-if="currentAccountId">
      <PendingEmailVerificationBanner v-if="hideOnOnboardingView" />
      <PaymentPendingBanner v-if="hideOnOnboardingView" />
    </template>
    <router-view v-slot="{ Component }">
      <transition name="fade" mode="out-in">
        <component :is="Component" />
      </transition>
    </router-view>
    <WootSnackbarBox />
    <NetworkNotification />
  </div>
  <LoadingState v-else />
</template>

<style lang="scss">
@import './assets/scss/app';

.v-popper--theme-tooltip .v-popper__inner {
  background: black !important;
  font-size: 0.75rem;
  padding: 4px 8px !important;
  border-radius: 6px;
  font-weight: 400;
}

.v-popper--theme-tooltip .v-popper__arrow-container {
  display: none;
}

.v-popper--theme-message-tooltip .v-popper__inner {
  background: #fff !important;
  color: #111 !important;
  font-size: 0.75rem;
  line-height: 1.4;
  white-space: pre-line;
  padding: 10px 12px !important;
  border-radius: 10px;
  border: 1px solid #111;
  box-shadow: 0 10px 25px rgba(15, 23, 42, 0.16);
  max-width: 20rem;
}

.v-popper--theme-message-tooltip .v-popper__arrow-container {
  display: block;
}

.v-popper--theme-message-tooltip .v-popper__arrow-inner {
  border-color: #fff !important;
}

.v-popper--theme-message-tooltip .v-popper__arrow-outer {
  border-color: #111 !important;
}
</style>
